import type { AdminCustomerCart } from "@hobbyshopify/backend-client";
import { useEffect, useRef, useState } from "react";
import { useObserver, useSubtree } from "subtree.js/react";
import { CustomerTabs } from "../../components/CustomerTabs";
import { LoadFailed, Loading } from "../../components/LoadState";
import { cardTitle, formatCents } from "../../core/primitives/card-display";
import { cartLinesTotalCents } from "../../core/primitives/customer-cart";
import { customerLabel } from "../../core/primitives/customer-label";
import { formatAmount } from "../../core/primitives/money";
import { CustomerQrModal } from "../../sections/customer-qr/customer-qr-modal";
import { type CartItem, CustomerCartsActions, CustomerCartsState } from "./customer-carts-model";
import { TerminalPaymentModal } from "./sections/terminal-payment/terminal-payment-modal";

type RequestStatus = AdminCustomerCart["status"];

// Open is the cart that still needs the merchant; paid is settled and locked.
// So open takes the accent and paid recedes — the opposite of what "done is
// green" would suggest, because this list exists to surface outstanding work.
const REQUEST_STATUS_STYLES: Record<RequestStatus, string> = {
  OPEN: "badge-accent",
  PAID: "badge-neutral",
  // Nobody came back to it, so the shop stopped holding the cards and released
  // the copies. Warn rather than neutral: it is the one state where the cards
  // in the list below are no longer reserved for this customer.
  EXPIRED: "badge-warn",
};

/**
 * How long the shop is still holding this cart.
 *
 * The same deadline the customer is watching, read from the same field, so the
 * two screens cannot tell different stories about when the cards go back on
 * sale. Adding a card or opening a payment pushes it out.
 */
function HoldRemaining({ expiresAt }: { expiresAt: Date | null }) {
  if (!expiresAt) return null;
  const minutes = Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 60_000);
  if (minutes <= 0) return null;
  return (
    <p className={`text-xs ${minutes <= 10 ? "text-warn" : "text-faint"}`}>
      Held for {minutes === 1 ? "less than a minute" : `${minutes} more minutes`}
    </p>
  );
}

function titleCase(s: string): string {
  return s.charAt(0) + s.slice(1).toLowerCase();
}

function Badge({ status, cls }: { status: string; cls: string }) {
  return <span className={`badge ${cls}`}>{titleCase(status)}</span>;
}

// Total of only the items that will appear in the Shopify draft order, in minor
// units — the offer's price is what the customer was quoted and what checkout
// charges, so nothing here re-derives it from a scan.

function itemSubtitle(item: CartItem): string {
  const card = item.offer.card;
  return [card.year || null, card.setName, card.parallelType].filter(Boolean).join(" · ");
}

function CloseIcon() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.75}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
    </svg>
  );
}

function ItemRow({
  item,
  busy,
  readOnly,
  canDelete,
  onToggleInclude,
  onDelete,
}: {
  item: CartItem;
  busy: boolean;
  readOnly: boolean;
  canDelete: boolean;
  onToggleInclude: (item: CartItem) => void;
  onDelete: (item: CartItem) => void;
}) {
  const excluded = !item.includedInPurchaseOrder;
  return (
    <div className="flex flex-col gap-2 rounded-btn border border-line p-2.5">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 shrink-0 overflow-hidden rounded-thumb bg-surface-strong">
          {item.offer.card.imageUrl ? (
            <img
              src={item.offer.card.imageUrl}
              alt={cardTitle(item.offer.card)}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-[10px] text-faint">No img</div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className={`truncate text-sm ${excluded ? "text-faint line-through" : "text-white"}`}>
            {cardTitle(item.offer.card)}
          </p>
          {itemSubtitle(item) && <p className="truncate text-xs text-faint">{itemSubtitle(item)}</p>}
        </div>
        {excluded && <Badge status="Excluded" cls="badge-neutral" />}
        {/* Nothing is reserved behind this line: the shop quoted a card it has
            none of on hand. Worth seeing here above all, because this is the
            moment somebody is about to hand the card over — but only for a card
            the shop actually counts, since an untracked one never holds a copy
            and saying so on every line would train people to ignore it. */}
        {item.offer.card.trackStock && item.offer.stockId === null && <Badge status="No copy held" cls="badge-warn" />}
        {/* Dimmed rather than accented when excluded — it is not part of the
            total the merchant is about to charge. */}
        <span className={`w-16 shrink-0 text-right text-sm font-medium ${excluded ? "text-faint" : "text-accent"}`}>
          {item.quantity > 1 && <span className="mr-1 text-faint">{item.quantity}×</span>}
          {formatCents(item.offer.priceCents)}
        </span>
      </div>

      {!readOnly && (
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex select-none items-center gap-1 text-xs text-muted">
            <input
              type="checkbox"
              checked={item.includedInPurchaseOrder}
              disabled={busy}
              onChange={() => onToggleInclude(item)}
              className="accent-accent"
            />
            In order
          </label>
          {canDelete && (
            <button
              type="button"
              onClick={() => onDelete(item)}
              disabled={busy}
              className="ml-auto shrink-0 p-1 text-faint transition-colors hover:text-danger disabled:opacity-40"
              aria-label={`Remove ${cardTitle(item.offer.card)}`}
              title="Remove item"
            >
              <CloseIcon />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function CustomerCartTile({
  request,
  busyItemId,
  busyRequest,
  confirmingDelete,
  deleteError,
  focused,
}: {
  request: AdminCustomerCart;
  busyItemId: string | null;
  busyRequest: boolean;
  confirmingDelete: boolean;
  deleteError: string | null;
  focused: boolean;
}) {
  const actions = useSubtree(CustomerCartsActions);
  // A lapsed cart is as settled as a paid one from the merchant's side: its
  // copies are back on sale, so editing it would be editing a set of cards the
  // shop is no longer holding.
  const readOnly = request.status !== "OPEN";
  const canDeleteItem = !readOnly && request.items.length > 1;
  const totalCents = cartLinesTotalCents(request);
  // Only once tax has actually been quoted onto a checkout. A checkout with no
  // tax on it falls back to the plain total: a shop that charges none has
  // nothing to break out, and a "Sales tax $0.00" line invites the question.
  const checkout = request.checkout;
  const quote =
    checkout && checkout.taxAmount !== null && checkout.subtotalAmount !== null && checkout.amountTotal !== null
      ? { ...checkout, taxAmount: checkout.taxAmount }
      : null;
  const [copied, setCopied] = useState(false);

  const handleCopyLink = async () => {
    if (!request.checkoutUrl) {
      return;
    }
    await navigator.clipboard.writeText(request.checkoutUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focused) ref.current?.scrollIntoView({ block: "center" });
  }, [focused]);

  return (
    <div ref={ref} className={`panel relative flex min-w-0 flex-col p-4 sm:p-5 ${focused ? "ring-2 ring-accent" : ""}`}>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-white">{customerLabel(request.user)}</p>
          {request.status === "OPEN" ? (
            <HoldRemaining expiresAt={request.expiresAt} />
          ) : (
            request.status === "EXPIRED" && (
              <p className="text-xs text-warn">The hold ran out — these copies are back on sale.</p>
            )
          )}
        </div>
        <Badge status={request.status} cls={REQUEST_STATUS_STYLES[request.status]} />
      </div>

      <div className="mb-4 space-y-2">
        {request.items.map((item) => (
          <ItemRow
            key={item.id}
            item={item}
            busy={busyRequest || confirmingDelete || busyItemId === item.id}
            readOnly={readOnly}
            canDelete={canDeleteItem}
            onToggleInclude={(it) => void actions.toggleInclude(request, it)}
            onDelete={(it) => void actions.removeItem(request, it)}
          />
        ))}
      </div>

      <div className="mb-4 border-t border-line pt-3">
        {quote ? (
          <>
            {/* The figures Stripe actually quoted, read off the checkout.
                They cannot describe a different cart than the one above:
                editing items cancels the checkout, and only a PENDING or
                PAID one reaches this DTO. */}
            <div className="flex justify-between py-0.5">
              <span className="text-xs text-faint">Cards</span>
              <span className="text-xs text-muted">{formatAmount(quote.subtotalAmount, quote.currency)}</span>
            </div>
            <div className="flex justify-between py-0.5">
              <span className="text-xs text-faint">Sales tax</span>
              <span className="text-xs text-muted">{formatAmount(quote.taxAmount, quote.currency)}</span>
            </div>
            {/* The customer's fees, each on its own line and never summed into
                one: the shop reconciles the technology fee against HobbyScan
                and the processing fee against Stripe's deduction. */}
            {quote.platformFeeAmount !== null && (
              <div className="flex justify-between py-0.5">
                <span className="text-xs text-faint">HobbyScan technology fee (paid by customer)</span>
                <span className="text-xs text-muted">{formatAmount(quote.platformFeeAmount, quote.currency)}</span>
              </div>
            )}
            {quote.processingFeeAmount !== null && (
              <div className="flex justify-between py-0.5">
                <span className="text-xs text-faint">Card processing fee (paid by customer)</span>
                <span className="text-xs text-muted">{formatAmount(quote.processingFeeAmount, quote.currency)}</span>
              </div>
            )}
            <div className="mt-1.5 flex justify-between">
              <span className="text-sm font-semibold text-muted">Purchase order total</span>
              <span className="text-base font-bold text-accent">{formatAmount(quote.amountTotal, quote.currency)}</span>
            </div>
          </>
        ) : (
          <>
            <div className="flex justify-between">
              <span className="text-sm font-semibold text-muted">Purchase order total</span>
              <span className="text-base font-bold text-accent">{formatCents(totalCents)}</span>
            </div>
            {/* No checkout yet, so there is no tax figure to show — quoting one
                per card on this list would bill the shop a Stripe Tax
                calculation for rendering a page. Saying it is coming is what
                stops the total above reading as the final word. */}
            {request.salesTaxAtCheckout && (
              <p className="mt-1 text-xs text-faint">Sales tax is added when you take payment.</p>
            )}
          </>
        )}
      </div>

      {!readOnly && (
        <button
          type="button"
          onClick={() => actions.takePayment(request)}
          disabled={busyRequest || confirmingDelete}
          className="btn-primary btn-xs mb-2 w-full"
        >
          Take payment in person
        </button>
      )}

      {request.checkoutUrl && !readOnly ? (
        <button
          type="button"
          onClick={handleCopyLink}
          disabled={busyRequest || confirmingDelete}
          className="btn-chip w-full justify-center truncate"
        >
          {copied ? "Link copied ✓" : "Copy checkout link"}
        </button>
      ) : null}

      {request.status === "PAID" && (
        <p className="mt-2 text-center text-xs text-faint">Paid — this request is locked.</p>
      )}

      {!readOnly && (
        <div className="mt-3 border-t border-line pt-3">
          {confirmingDelete ? (
            <div className="note note-danger p-3">
              <p className="text-sm font-semibold text-danger">Delete this request?</p>
              <p className="mt-1 text-xs leading-5 text-muted">
                This removes every item and deletes its Shopify draft order. This cannot be undone.
              </p>
              {deleteError && (
                <p role="alert" className="mt-2 text-xs font-medium text-danger">
                  {deleteError}
                </p>
              )}
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={actions.cancelDelete}
                  disabled={busyRequest}
                  className="btn-secondary btn-xs flex-1"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void actions.confirmDelete(request)}
                  disabled={busyRequest}
                  className="btn-destructive btn-xs flex-1"
                >
                  {busyRequest ? "Deleting…" : "Delete request"}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => actions.promptDelete(request)}
              disabled={busyRequest}
              className="btn-ghost hover:text-danger"
            >
              <TrashIcon />
              Delete request
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function CustomerCartsPage() {
  const state = useSubtree(CustomerCartsState);
  const actions = useSubtree(CustomerCartsActions);
  const observer = useObserver();
  const shopId = observer.watch(state.shopId);
  const status = observer.watch(state.status);
  const requests = observer.watch(state.carts);
  const busyItemId = observer.watch(state.busyItemId);
  const busyCartId = observer.watch(state.busyCartId);
  const confirmDeleteId = observer.watch(state.confirmDeleteId);
  const deleteError = observer.watch(state.deleteError);
  const actionError = observer.watch(state.actionError);
  const payingRequest = observer.watch(state.payingCart);
  const openCarts = observer.watch(state.openCarts);

  if (!shopId) {
    return (
      <div className="mx-auto max-w-7xl px-2 py-4 sm:px-6 sm:py-8 lg:px-8">
        <h1 className="mb-4 text-xl font-bold text-white">Customer Carts</h1>
        <p className="text-sm text-muted">We're still loading your shop. Refresh if this doesn't clear.</p>
      </div>
    );
  }

  const isLoading = status.kind === "idle" || status.kind === "loading";
  const isError = status.kind === "error";

  return (
    <div className="mx-auto max-w-7xl px-2 py-4 sm:px-6 sm:py-8 lg:px-8">
      <CustomerTabs active="carts" openCarts={openCarts} onShowQr={actions.showQr} qrDisabled={!shopId} />
      <CustomerQrModal />

      {actionError && (
        <p role="alert" className="note note-danger mb-4 text-danger">
          {actionError}
        </p>
      )}

      {isLoading && <Loading label="Loading customer carts…" />}

      {status.kind === "error" && (
        <LoadFailed title="Couldn't load customer carts." message={status.message} onRetry={actions.reload} />
      )}

      {!isLoading && !isError && requests.length === 0 && (
        <div className="panel p-12 text-center">
          <p className="mb-1 text-base font-medium text-white">No customer carts yet</p>
          <p className="text-sm text-muted">Requests submitted from the app will appear here.</p>
        </div>
      )}

      {!isLoading && !isError && requests.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {requests.map((request) => (
            <CustomerCartTile
              key={request.id}
              request={request}
              busyItemId={busyItemId}
              busyRequest={busyCartId === request.id}
              confirmingDelete={confirmDeleteId === request.id}
              deleteError={deleteError?.id === request.id ? deleteError.message : null}
              focused={state.focusCartId === request.id}
            />
          ))}
        </div>
      )}

      {payingRequest && (
        <TerminalPaymentModal
          customerCartId={payingRequest.id}
          fallbackTotalCents={cartLinesTotalCents(payingRequest)}
        />
      )}
    </div>
  );
}
